// src/auth.js — Dual-mode authentication (OIDC Keycloak + Legacy Mehrwert OAuth)
import {
  OIDC_ENABLED,
  OIDC_ISSUER,
  OIDC_CLIENT_ID,
  OIDC_SCOPE,
  OIDC_REDIRECT_URI,
  CLIENT_ID,
  SIMBA_SERVICE_BASE_URL,
  OAUTH_AUTH_URL,
  SSO_LOGOUT_URL,
  AUTH_ENABLED
} from './config'
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState
} from './oidc/pkce'
import {
  oidcTokenExchange,
  legacyTokenExchange,
  revokeToken
} from './oidc/auth'
import { clearTermsOfUseAcceptance } from './agb'

// ── Token storage ──────────────────────────────────────────────

/**
 * Retrieve the current access token from localStorage.
 * @returns {string|null}
 */
export function getAccessToken() {
  return localStorage.getItem('access_token')
}

function getRefreshToken() {
  return localStorage.getItem('refresh_token')
}

/** True when either access or refresh token is present in storage. */
export function hasStoredAuthTokens() {
  return !!(getAccessToken() || getRefreshToken())
}

/** Seconds before JWT exp to treat the access token as expired. */
const TOKEN_EXPIRY_SKEW_SEC = 30

function parseJwtPayload(token) {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }
    const base64Url = parts[1]
    const base64 =
      base64Url.replace(/-/g, '+').replace(/_/g, '/') +
      '='.repeat((4 - (base64Url.length % 4)) % 4)
    return JSON.parse(atob(base64))
  } catch (e) {
    return null
  }
}

function isAccessTokenExpired(token, skewSeconds) {
  skewSeconds = skewSeconds || TOKEN_EXPIRY_SKEW_SEC
  const payload = parseJwtPayload(token)
  if (!payload || !payload.exp) {
    return false
  }
  return Date.now() >= (payload.exp - skewSeconds) * 1000
}

function setTokens(accessToken, refreshToken, idToken) {
  localStorage.setItem('access_token', accessToken)
  if (refreshToken) localStorage.setItem('refresh_token', refreshToken)
  if (idToken) localStorage.setItem('id_token', idToken)
}

function clearTokens() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('id_token')
  clearOidcSession()
}

/** Clear auth tokens and client-side terms-of-use acceptance. */
function clearLocalSessionState() {
  clearTokens()
  clearTermsOfUseAcceptance()
}

// ── Circuit breaker ────────────────────────────────────────────
let authLoopCount = 0
let authLoopTimestamp = 0
const AUTH_LOOP_MAX = 3
const AUTH_LOOP_WINDOW_MS = 30000

function checkAuthLoop() {
  const now = Date.now()
  if (now - authLoopTimestamp > AUTH_LOOP_WINDOW_MS) {
    authLoopCount = 0
    authLoopTimestamp = now
  }
  authLoopCount++
  authLoopTimestamp = now
  if (authLoopCount > AUTH_LOOP_MAX) {
    console.error(
      'Auth redirect loop: ' +
        authLoopCount +
        ' in ' +
        AUTH_LOOP_WINDOW_MS / 1000 +
        's.'
    )
    clearLocalSessionState()
    window.location.href = window.location.origin + '/logout?loop=true'
    return false
  }
  return true
}

function resetAuthLoop() {
  authLoopCount = 0
  authLoopTimestamp = 0
}

// ── State ──────────────────────────────────────────────────────
let currentState = null
let codeVerifier = null
let refreshInFlight = null

// ── OIDC redirect ──────────────────────────────────────────────
async function oidcRedirectToAuthorize() {
  codeVerifier = generateCodeVerifier()
  currentState = generateState()
  sessionStorage.setItem('oidc_code_verifier', codeVerifier)
  sessionStorage.setItem('oidc_state', currentState)
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  const params = new URLSearchParams()
  params.append('response_type', 'code')
  params.append('client_id', OIDC_CLIENT_ID)
  params.append('redirect_uri', OIDC_REDIRECT_URI)
  params.append('scope', OIDC_SCOPE)
  params.append('state', currentState)
  params.append('code_challenge', codeChallenge)
  params.append('code_challenge_method', 'S256')

  window.location.href =
    OIDC_ISSUER + '/protocol/openid-connect/auth?' + params.toString()
}

// ── Legacy redirect ────────────────────────────────────────────
function legacyRedirectToAuthorize() {
  currentState = 'test'
  window.location.href =
    OAUTH_AUTH_URL +
    '/authorize/verified-by-gs1-free/?response_type=code&client_id=' +
    CLIENT_ID +
    '&state=' +
    currentState +
    '&redirect_url=' +
    encodeURIComponent(SIMBA_SERVICE_BASE_URL)
}

// ── Public: Redirect ───────────────────────────────────────────

/**
 * Initiate the authorization redirect to the identity provider.
 * Picks OIDC or legacy flow based on the OIDC_ENABLED feature flag.
 * Includes a circuit breaker to detect redirect loops.
 */
export async function redirectToAuthorize() {
  if (!checkAuthLoop()) return
  clearTokens()
  if (OIDC_ENABLED) {
    await oidcRedirectToAuthorize()
  } else {
    legacyRedirectToAuthorize()
  }
}

// ── Internal: Token exchange ───────────────────────────────────
async function callTokenEndpoint(params, redirectOnFail) {
  const exchangeFn = OIDC_ENABLED ? oidcTokenExchange : legacyTokenExchange
  const data = await exchangeFn(params)
  if (!data) {
    if (redirectOnFail) redirectToAuthorize()
    return false
  }
  setTokens(data.access_token, data.refresh_token, data.id_token)
  resetAuthLoop()
  return true
}

// ── Public: OIDC query helpers ─────────────────────────────────

const OIDC_CALLBACK_QUERY_KEYS = [
  'code',
  'state',
  'error',
  'error_description',
  'session_state'
]

/**
 * Remove OIDC authorization callback query params so they are not carried
 * into downstream navigations (e.g. TermsOfUse after first login).
 */
export function stripOidcCallbackParams(query) {
  const q = Object.assign({}, query || {})
  OIDC_CALLBACK_QUERY_KEYS.forEach(function (key) {
    delete q[key]
  })
  return q
}

/** True when the query object contains any OIDC authorization callback param. */
export function hasOidcCallbackParams(query) {
  if (!query) return false
  return OIDC_CALLBACK_QUERY_KEYS.some(function (key) {
    return query[key] !== undefined
  })
}

// ── Public: Authenticate ───────────────────────────────────────

/**
 * Authenticate the user with an authorization code, or check existing tokens.
 *
 * Called by the OidcCallback/Token views after the authorization server redirects
 * back with a code.  When called without a code, checks for existing tokens
 * in localStorage and attempts a refresh if only a refresh_token is present.
 *
 * When OIDC is enabled and tokens already exist, a callback `code` is treated as
 * stale (e.g. left over in the URL after a prior exchange) and the refresh path
 * is used instead of re-exchanging the authorization code.
 *
 * @param {string} [code] — authorization code from the callback query string
 * @param {string} [state] — state parameter for OIDC CSRF protection
 * @returns {Promise<boolean>} true if authenticated (or AUTH_ENABLED is false)
 */
export async function authenticate(code, state) {
  if (!AUTH_ENABLED) return true

  const staleCallbackWithSession =
    code && OIDC_ENABLED && hasStoredAuthTokens()

  if (code && !staleCallbackWithSession) {
    // Restore from sessionStorage if the page was refreshed
    if (!currentState) {
      currentState = sessionStorage.getItem('oidc_state')
    }
    if (!codeVerifier) {
      codeVerifier = sessionStorage.getItem('oidc_code_verifier')
    }

    if (OIDC_ENABLED) {
      // P13: Strict state validation for OIDC
      if (state && state !== currentState) {
        console.error('OIDC state mismatch! Aborting login.')
        clearOidcSession()
        redirectToAuthorize()
        return false
      }
      const params = new URLSearchParams()
      params.append('grant_type', 'authorization_code')
      params.append('code', code)
      params.append('redirect_uri', OIDC_REDIRECT_URI)
      params.append('code_verifier', codeVerifier || '')
      const ok = await callTokenEndpoint(params, true)
      clearOidcSession()
      return ok
    }

    // Legacy flow: warn-only for state mismatch (Mehrwert may not send state)
    if (state && state !== currentState) {
      console.warn(
        'Legacy state mismatch — continuing (Mehrwert may not send state)'
      )
    }
    const params = new URLSearchParams()
    params.append('code', code)
    params.append('grant_type', 'authorization_code')
    params.append('client_id', CLIENT_ID)
    const ok = await callTokenEndpoint(params, true)
    clearOidcSession()
    return ok
  }

  // No authorization code — or stale callback params with an existing session
  const storedAccessToken = getAccessToken()
  const storedRefreshToken = getRefreshToken()
  if (!storedAccessToken && !storedRefreshToken) {
    redirectToAuthorize()
    return false
  }
  if (
    storedRefreshToken &&
    (!storedAccessToken || isAccessTokenExpired(storedAccessToken))
  ) {
    return refresh()
  }
  return true
}

function clearOidcSession() {
  currentState = null
  codeVerifier = null
  sessionStorage.removeItem('oidc_state')
  sessionStorage.removeItem('oidc_code_verifier')
}

// ── Public: Refresh ────────────────────────────────────────────

async function exchangeRefreshToken() {
  const storedRefreshToken = getRefreshToken()
  if (!storedRefreshToken) {
    await redirectToAuthorize()
    return false
  }
  const params = new URLSearchParams()
  params.append('refresh_token', storedRefreshToken)
  params.append('grant_type', 'refresh_token')
  if (!OIDC_ENABLED) {
    params.append('client_id', CLIENT_ID)
  }
  return callTokenEndpoint(params, true)
}

/**
 * Attempt to refresh the access token using a stored refresh_token.
 * Concurrent callers share one in-flight exchange so refresh-token rotation
 * cannot invalidate a token that another request already refreshed.
 * Redirects to the authorize endpoint if no refresh_token is available.
 *
 * @returns {Promise<boolean>} true if the refresh succeeded
 */
export function refresh() {
  if (!refreshInFlight) {
    refreshInFlight = exchangeRefreshToken().finally(function () {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/**
 * Refresh the access token when it is missing or near expiry.
 * Call before API requests so expired tokens are renewed without hitting 401.
 *
 * @returns {Promise<boolean>} true when a valid access token is available or auth is disabled
 */
export async function ensureValidAccessToken() {
  if (!AUTH_ENABLED) {
    return true
  }
  const storedAccessToken = getAccessToken()
  const storedRefreshToken = getRefreshToken()
  if (!storedAccessToken && !storedRefreshToken) {
    return true
  }
  if (storedAccessToken && !isAccessTokenExpired(storedAccessToken)) {
    return true
  }
  if (storedRefreshToken) {
    return refresh()
  }
  await redirectToAuthorize()
  return false
}

/**
 * Recover from an API 401 or opaque network failure on an authenticated request.
 * @returns {Promise<boolean>} true if a new access token is available
 */
export async function recoverFromUnauthorized(hadAccessToken) {
  if (!AUTH_ENABLED) {
    return false
  }
  if (getRefreshToken()) {
    return refresh()
  }
  if (hadAccessToken) {
    await redirectToAuthorize()
  }
  return false
}

// ── Public: Logout ─────────────────────────────────────────────

/**
 * Log the user out by clearing local session state (tokens, OIDC session,
 * terms-of-use acceptance) and redirecting to the identity provider's logout
 * endpoint.
 *
 * For OIDC the token is **not** revoked — the IdP session logout is sufficient.
 * For legacy OAuth the token is revoked before redirect.
 */
export function logout() {
  const storedAccessToken = getAccessToken()
  const storedIdToken = localStorage.getItem('id_token')
  clearLocalSessionState()

  if (OIDC_ENABLED) {
    // After Keycloak redirects back to /logout, the LogoutView will read
    // this and send the user to the final logout landing page.
    sessionStorage.setItem('post_oidc_logout_url', SSO_LOGOUT_URL)
    const postLogoutUri = window.location.origin + '/logout'
    if (storedIdToken) {
      const params = new URLSearchParams()
      params.append('post_logout_redirect_uri', postLogoutUri)
      params.append('id_token_hint', storedIdToken)
      window.location.href =
        OIDC_ISSUER + '/protocol/openid-connect/logout?' + params.toString()
    } else {
      // No id_token_hint — go straight to /logout so it can redirect to
      // the final logout landing page.
      window.location.href = postLogoutUri
    }
    return
  }

  // Legacy mode: revoke token before redirect
  if (storedAccessToken) revokeToken(storedAccessToken)
  window.location.href = SSO_LOGOUT_URL
}



// WEBPACK FOOTER //
// ./src/auth.js